import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { customersApi, ordersApi } from '../api';
import toast from 'react-hot-toast';

export default function CustomersDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('shirt');
  const [orderCount, setOrderCount] = useState(0);

  useEffect(() => {
    fetchCustomer();
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const [customerRes, ordersRes] = await Promise.all([
        customersApi.getById(id),
        ordersApi.getAll({ customerId: id, limit: 1 })
      ]);

      console.log('Customer API Response:', customerRes);
      console.log('Orders API Response:', ordersRes);

      // Handle customer data
      let customerData;
      if (customerRes?.data?.data?.customer) {
        customerData = customerRes.data.data.customer;
      } else if (customerRes?.data?.data) {
        customerData = customerRes.data.data;
      } else if (customerRes?.data) {
        customerData = customerRes.data;
      }
      setCustomer(customerData);

      // Handle orders count
      const ordersData = ordersRes?.data?.data?.orders || ordersRes?.data?.orders || ordersRes?.data || [];
      const pagination = ordersRes?.data?.pagination;
      setOrderCount(pagination?.total || (Array.isArray(ordersData) ? ordersData.length : 0));

    } catch (err) {
      console.error('Error fetching customer:', err);
      toast.error(err.response?.data?.message || 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const hasMeasurements = (customer) => {
    if (!customer?.measurements) return false;
    return Object.keys(customer.measurements).some(type =>
      Object.values(customer.measurements[type] || {}).some(val => val)
    );
  };

  const getMeasurementTypes = (measurements) => {
    if (!measurements) return [];
    return Object.keys(measurements);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-screen">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-icon">👤</div>
          <p>Customer not found</p>
          <Link to="/customers" className="btn btn-primary">Back to Customers</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Link to="/customers" className="btn btn-secondary btn-sm">← Back</Link>
          <div>
            <h1 className="page-title" style={{ marginBottom: 4 }}>
              {customer.name}
            </h1>
            <span className={`badge ${hasMeasurements(customer) ? 'badge-ready' : 'badge-pending'}`}>
              {hasMeasurements(customer) ? 'Measurements Recorded' : 'Missing Measurements'}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => navigate(`/customers?edit=${customer.id}`)}
          >
            Edit Customer
          </button>
        </div>
      </div>

      <div className="grid-2" style={{ gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Left Column */}
        <div className="flex flex-col gap-4">
          {/* Customer Info Card */}
          <div className="card p-2">
            <h3 className="card-title">Customer Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Full Name</span>
                <span className="info-value font-semibold">{customer.name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Phone</span>
                <span className="info-value font-semibold">{customer.phone}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Email</span>
                <span className="info-value">{customer.email || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Address</span>
                <span className="info-value">{customer.address || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Created At</span>
                <span className="info-value">{formatDate(customer.createdAt)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Updated At</span>
                <span className="info-value">{formatDate(customer.updatedAt)}</span>
              </div>
            </div>
          </div>

          {/* Measurements Card */}
          {customer.measurements && (
            <div className="card p-2">
              <h3 className="card-title">Body Measurements (inches)</h3>

              {/* Measurement Type Tabs */}
              <div className="measurement-tabs">
                {getMeasurementTypes(customer.measurements).map((type) => (
                  <button
                    key={type}
                    className={`tab-btn `}
                    onClick={() => setActiveTab(type)}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>

              {/* Measurement Values */}
              <div className="measurements-grid">
                {customer.measurements[activeTab] && Object.entries(customer.measurements[activeTab]).map(([key, value]) => (
                  <div key={key} className="measurement-item">
                    <span className="measurement-label">{key.replace(/_/g, ' ')}</span>
                    <span className="measurement-value">{value || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-4">
          {/* Quick Stats Card */}
          <div className="card p-2">
            <h3 className="card-title">Quick Information</h3>
            <div className="summary-table">
              <div className="summary-row">
                <span>Customer ID</span>
                <span className="mono text-muted">#{customer.id}</span>
              </div>
              <div className="summary-row">
                <span>Phone</span>

                <span className="mono text-muted">{customer.phone}</span>
              </div>
              <div className="summary-row">
                <span>Measurements</span>
                <span className={`badge ${hasMeasurements(customer) ? 'badge-ready' : 'badge-pending'}`}>
                  {hasMeasurements(customer) ? 'Recorded' : 'Missing'}
                </span>
              </div>
              <div className="summary-row">
                <span>Email</span>
                <span>{customer.email || '—'}</span>
              </div>
              <div className="summary-row total">
                <span>Total Orders</span>
                <span className="font-bold">{orderCount}</span>
              </div>
            </div>
          </div>

          {/* Actions Card */}
          <div className="card p-2">
            <h3 className="card-title">Actions</h3>
            <div className="flex flex-col gap-2">
              <button
                className="btn btn-secondary"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => navigate(`/customers?edit=${customer.id}`)}
              >
                ✏️ Edit Customer
              </button>
              <button
                className="btn btn-secondary"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => navigate(`/orders?customerId=${customer.id}`)}
              >
                📋 View Orders
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .grid-2 {
          display: grid;
        }
        @media (max-width: 1024px) {
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
        }
        .flex {
          display: flex;
        }
        .flex-col {
          flex-direction: column;
        }
        .items-center {
          align-items: center;
        }
        .gap-2 {
          gap: 8px;
        }
        .gap-4 {
          gap: 16px;
        }
        .card-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--border-color);
        }
        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }
        .info-item {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .info-label {
          font-size: 0.75rem;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .info-value {
          font-size: 0.9rem;
          color: var(--text-primary);
        }
        .summary-table {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          font-size: 0.9rem;
          padding: 8px 0;
          border-bottom: 1px solid var(--border-color);
        }
        .summary-row:last-child {
          border-bottom: none;
        }
        .summary-row.total {
          font-size: 1rem;
          padding-top: 12px;
          margin-top: 4px;
          border-top: 2px solid var(--border-color);
        }
        .measurement-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
        }
        .tab-btn {
          padding: 8px 16px;
          border: 1px solid var(--border-color);
          background: var(--bg-secondary);
          color: var(--text-secondary);
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 500;
          transition: all 0.2s;
        }
        .tab-btn:hover {
          background: var(--bg-hover);
        }
        .tab-btn.active {
          background: var(--primary);
          color: white;
          border-color: var(--primary);
        }
        .measurements-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }
        .measurement-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 12px;
          background: var(--bg-secondary);
          border-radius: 8px;
        }
        .measurement-label {
          font-size: 0.7rem;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .measurement-value {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text-primary);
        }
        .badge-active { background: #d1fae5; color: #065f46; }
        .badge-inactive { background: #fee2e2; color: #991b1b; }
        .badge-pending { background: #fef3c7; color: #92400e; }
        .badge-stitching { background: #dbeafe; color: #1e40af; }
        .badge-qc { background: #e0e7ff; color: #3730a3; }
        .badge-ready { background: #d1fae5; color: #065f46; }
        .badge-delivered { background: #dcfce7; color: #166534; }
        .badge-cancelled { background: #fee2e2; color: #991b1b; }
        .badge-in_progress { background: #dbeafe; color: #1e40af; }
        .badge-done { background: #d1fae5; color: #065f46; }
      `}</style>
    </div>
  );
}
